import type { PatientInfo } from '@/api/types/PatientApi'
import config from '@/config'
import { registerHeapAutocapture } from '@heap/heap-react-native-autocapture'
import { Heap } from '@heap/heap-react-native-bridge'
import { Platform } from 'react-native'

interface HeapAPI {
	identify: (userId: string) => void
	addUserProperties: (properties: Record<string, string>) => void
	track: (event: string, properties?: Record<string, string | number | boolean>) => void
	load: (appId: string) => void
}

declare global {
	interface Window {
		heap?: HeapAPI
	}
}

const HEAP_APP_ID = '3775159598'
const HEAP_SCRIPT = `window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])}`

const toHeapId = (patientId: number) => `${config.HEAP_ID_PREFIX}-${patientId}`

const toHeapProps = (info: PatientInfo) => {
	const props: Record<string, string> = {}
	if (info.email) props['email'] = info.email
	if (info.phone) props['phone'] = info.phone
	return props
}

let isBootstrapped = false

const bootstrapWebHeap = () => {
	if (window.heap) return

	const script = document.createElement('script')
	script.innerHTML = `
    (function() {
      window.heap=${HEAP_SCRIPT};
      window.heap.load("${HEAP_APP_ID}");
    })();
    true;
  `
	script.type = 'text/javascript'
	script.async = true
	document.head.appendChild(script)
}

const bootstrapNativeHeap = () => {
	Heap.startRecording(HEAP_APP_ID)
	registerHeapAutocapture(true)
}

const waitForNativeHeapReady = (timeoutInMs = 5000): Promise<void> => {
	return new Promise((resolve) => {
		let attempts = 0
		const maxAttempts = Math.floor(timeoutInMs / 200)

		const checkIsReady = async () => {
			try {
				if (await Heap.getEnvironmentId()) {
					resolve()
					return
				}
			} catch {}

			if (++attempts < maxAttempts) {
				setTimeout(checkIsReady, 200)
			}
		}

		checkIsReady()
	})
}

const waitForWebHeapReady = (timeoutInMs = 5000): Promise<void> => {
	return new Promise((resolve) => {
		const interval = setInterval(() => {
			const heap = window.heap
			if (typeof heap?.identify === 'function') {
				clearInterval(interval)
				resolve()
			}
		}, 200)

		setTimeout(() => {
			clearInterval(interval)
		}, timeoutInMs)
	})
}

export default {
	bootstrap() {
		if (isBootstrapped) return

		if (Platform.OS === 'web') {
			bootstrapWebHeap()
		} else {
			bootstrapNativeHeap()
		}

		isBootstrapped = true
	},

	identify(patientInfo: PatientInfo) {
		if (!patientInfo) return

		const userId = toHeapId(patientInfo.id)
		const props = toHeapProps(patientInfo)

		if (Platform.OS === 'web') {
			waitForWebHeapReady().then(() => {
				window.heap?.identify?.(userId)
				window.heap?.addUserProperties?.(props)
			})
		} else {
			waitForNativeHeapReady().then(() => {
				Heap.identify(userId)
				Heap.addUserProperties(props)
			})
		}
	},

	track(event: string, properties?: Record<string, string | number | boolean>) {
		if (Platform.OS === 'web') {
			waitForWebHeapReady().then(() => {
				window.heap?.track?.(event, properties)
			})
		} else {
			waitForNativeHeapReady().then(() => {
				Heap.track(event, properties)
			})
		}
	},
}
